import { useAdminUsers, useBlockUser, useUnblockUser } from '@/api/admin';
import { useMe } from '@/api/me';
import { Badge } from '@/components/library/ui/Badge';
import { ConfirmDialog } from '@/components/library/ui/ConfirmDialog';
import { Skeleton } from '@/components/library/ui/Skeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { AdminUser } from '@ultimate-checklist/shared';
import { ShieldX, UserCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/admin/')({
  component: AdminPage,
});

/** Admin-only. Non-admins are bounced home (the API also 403s, defense in depth). */
function AdminPage() {
  useDocumentTitle('Admin');
  const me = useMe();
  const navigate = useNavigate();

  useEffect(() => {
    if (me.data && !me.data.isAdmin) {
      void navigate({ to: '/', replace: true });
    }
  }, [me.data, navigate]);

  if (me.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (!me.data?.isAdmin) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Admin</h1>
        <p className="text-sm text-[var(--muted-foreground)]">
          Manage who can use the site. Blocking a user denies them all access until you unblock
          them.
        </p>
      </div>
      <AdminUsersTable currentUserId={me.data.id} />
    </div>
  );
}

function AdminUsersTable({ currentUserId }: { currentUserId: string }) {
  const users = useAdminUsers();
  const blockUser = useBlockUser();
  const unblockUser = useUnblockUser();
  const [confirm, setConfirm] = useState<AdminUser | null>(null);

  async function onBlock(user: AdminUser) {
    try {
      await blockUser.mutateAsync(user.id);
      setConfirm(null);
      toast.success(`Blocked ${user.email}`);
    } catch {
      toast.error('Couldn’t block that user');
    }
  }

  async function onUnblock(user: AdminUser) {
    try {
      await unblockUser.mutateAsync(user.id);
      toast.success(`Unblocked ${user.email}`);
    } catch {
      toast.error('Couldn’t unblock that user');
    }
  }

  if (users.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (users.isError) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-[var(--muted-foreground)]">
          Couldn’t load users.
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="divide-y divide-[var(--border)] overflow-hidden rounded-xl border border-[var(--border)]">
        {(users.data ?? []).map((user) => {
          const blocked = user.blockedAt !== null;
          const isSelf = user.id === currentUserId;
          return (
            <div
              key={user.id}
              className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-sm font-medium">{user.displayName || user.email}</p>
                  {user.isAdmin ? <Badge variant="secondary">Admin</Badge> : null}
                  {blocked ? (
                    <Badge variant="outline" className="border-red-300 text-red-600">
                      Blocked
                    </Badge>
                  ) : null}
                </div>
                <p className="truncate text-xs text-[var(--muted-foreground)]">
                  {user.email} · joined {formatDate(user.createdAt)}
                </p>
              </div>
              {user.isAdmin || isSelf ? (
                <span className="text-xs text-[var(--muted-foreground)]">
                  {isSelf ? 'You' : 'Admin'}
                </span>
              ) : blocked ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={unblockUser.isPending}
                  onClick={() => void onUnblock(user)}
                >
                  <UserCheck />
                  Unblock
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-[var(--destructive)]"
                  onClick={() => setConfirm(user)}
                >
                  <ShieldX />
                  Block
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(open) => !open && setConfirm(null)}
        title="Block this user?"
        description={
          confirm ? `${confirm.email} will lose all access to the site until you unblock them.` : ''
        }
        confirmLabel="Block user"
        destructive
        pending={blockUser.isPending}
        onConfirm={() => {
          if (confirm) {
            void onBlock(confirm);
          }
        }}
      />
    </>
  );
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(value));
}
